import { type FC, memo, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import BaseModal from 'components/BaseModal'
import Button from 'components/Button'
import ProgressIndicator from 'components/ProgressIndicator'
import Spacer from 'components/Spacer'
import { Body, Heading } from 'components/Text'
import { FlexDivCol } from 'components/layout/flex'
import { EVENT_NAME } from 'constants/matomoEvents'
import Connector from 'containers/Connector'
import { useAnalytics } from 'hooks/useAnalytics'
import { setOpenModal } from 'state/app/reducer'
import { onboardCrossMargin } from 'state/futures/actions'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectAbstractionWalletEnabled } from 'state/wallet/selectors'
import { ExternalLink } from 'styles/common'
import media from 'styles/media'

type Props = {
	onDismiss(): void
}

const AcknowledgementTerms: FC<Props> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const { trackAcknowledgeTerms, trackAccountCreation } = useAnalytics()
	const dispatch = useAppDispatch()
	const { logout } = Connector.useContainer()
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)

	const handleDisconnect = useCallback(() => {
		trackAcknowledgeTerms(EVENT_NAME.SIGN_OUT)
		logout()
		dispatch(setOpenModal(null))
	}, [logout, dispatch, trackAcknowledgeTerms])

	const handleAccept = useCallback(() => {
		trackAcknowledgeTerms(EVENT_NAME.AGREE_AND_CONTINUE)
		dispatch(setOpenModal('futures_create_account'))

		if (abstractionWalletEnabled) {
			dispatch(onboardCrossMargin())
			trackAccountCreation(EVENT_NAME.SUBMIT_CREATE_ACCOUNT)
		}
	}, [abstractionWalletEnabled, dispatch, trackAcknowledgeTerms, trackAccountCreation])

	return (
		<StyledBaseModal title="" onDismiss={onDismiss} showCross={false} transparentOverlay={false}>
			<Container>
				<ProgressIndicator currentStep={1} totalSteps={3} />
				<Heading variant="h5">{t('account.acknowledge-terms.title')}</Heading>
				<Divider height={1} divider />
				<FlexDivCol rowGap="12px">
					<TextContainer color="secondary">
						<Trans
							i18nKey="account.acknowledge-terms.copy"
							components={[<LinkText key="terms" href={'/terms'} />]}
						/>
					</TextContainer>
					<TextContainer color="secondary">
						{t('account.acknowledge-terms.risk-check-box')}
					</TextContainer>
					<TextContainer color="secondary">
						{t('account.acknowledge-terms.country-check-box')}
					</TextContainer>
				</FlexDivCol>
				<FlexDivCol rowGap="16px">
					<Button
						variant="solid-highlight"
						onClick={handleAccept}
						data-testid="acknowledge-terms-accept-button"
					>
						{t('account.acknowledge-terms.agree-and-continue')}
					</Button>
					<Button variant="flat" onClick={handleDisconnect}>
						{t('account.acknowledge-terms.sign-out')}
					</Button>
				</FlexDivCol>
			</Container>
		</StyledBaseModal>
	)
})

const Container = styled(FlexDivCol)`
	row-gap: 24px;
	padding: 10px;
`

const Divider = styled(Spacer)`
	width: 100%;
`

const LinkText = styled(ExternalLink)`
	color: ${(props) => props.theme.colors.common.palette.cyan.c500};
	font-weight: 600;
`

const TextContainer = styled(Body)`
	line-height: 18px;
`

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		max-width: 550px;
		margin-top: 250px;
	}

	${media.lessThan('md')`
		[data-reach-dialog-content] {
			max-width: 100%;
		}
	`}
`

export default AcknowledgementTerms
